/**
 * AddOnSelector Component
 * Checklist of available add-ons with descriptions and tooltips
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Checkbox,
  FormControlLabel,
  Tooltip,
  IconButton,
  Stack,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Info as InfoIcon,
  Email as EmailIcon,
  Assignment as AssignmentIcon,
  Shield as ShieldIcon,
  Build as BuildIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { SKUS, formatPrice, type Sku } from '../../lib/catalog';
import { useCartStore } from '../../store/cart';
import { trackCartAdd, trackCartRemove } from '../../lib/analytics';

interface AddOnSelectorProps {
  excludeSkuIds?: string[];
  onAddOnToggle?: (skuId: string, added: boolean) => void;
}

const AddOnSelector: React.FC<AddOnSelectorProps> = ({
  excludeSkuIds = ['core_build'],
  onAddOnToggle,
}) => {
  const theme = useTheme();
  const { items, add, remove } = useCartStore();

  // Get add-on SKUs (excluding core build and any specified exclusions)
  const addOns = Object.values(SKUS).filter(
    sku => !excludeSkuIds.includes(sku.id)
  );

  const isInCart = (skuId: string) => items.some(item => item.skuId === skuId);

  const handleToggle = (sku: Sku) => {
    const inCart = isInCart(sku.id);
    
    if (inCart) {
      remove(sku.id);
      trackCartRemove(sku.id, sku.name);
    } else {
      add(sku.id, 1);
      trackCartAdd(sku.id, sku.name, 1, sku.unitAmount);
    }
    
    onAddOnToggle?.(sku.id, !inCart);
  };

  // Icon mapping for visual appeal
  const getIcon = (skuId: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      deliverability: <EmailIcon />,
      ops_enablement: <AssignmentIcon />,
      audit_vault: <ShieldIcon />,
      maintenance_std: <BuildIcon />,
      hourly: <TimeIcon />,
      retainer_4h: <TimeIcon />,
    };
    return iconMap[skuId] || <InfoIcon />;
  };

  // Group by type for better organization
  const oneTimeAddOns = addOns.filter(sku => sku.type === 'one_time');
  const subscriptionAddOns = addOns.filter(sku => sku.type === 'subscription');
  const meteredAddOns = addOns.filter(sku => sku.type === 'metered');

  const renderAddOnCard = (sku: Sku) => {
    const checked = isInCart(sku.id);
    
    return (
      <Card
        key={sku.id}
        sx={{
          cursor: 'pointer',
          border: checked
            ? `2px solid ${theme.palette.primary.main}`
            : `1px solid ${theme.palette.divider}`,
          bgcolor: checked
            ? alpha(theme.palette.primary.main, 0.05)
            : 'background.paper',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: theme.palette.primary.main,
            boxShadow: theme.shadows[2],
          },
        }}
        onClick={() => handleToggle(sku)}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Checkbox
              checked={checked}
              onChange={() => handleToggle(sku)}
              onClick={(e) => e.stopPropagation()}
              sx={{ mt: -0.5 }}
            />
            
            <Box
              sx={{
                color: checked ? 'primary.main' : 'text.secondary',
                mt: 0.5,
              }}
            >
              {getIcon(sku.id)}
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {sku.name}
                </Typography>
                
                {sku.popular && (
                  <Chip
                    label="Popular"
                    size="small"
                    color="warning"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
                
                <Tooltip title={sku.features?.join(' • ') || sku.description} arrow>
                  <IconButton size="small" onClick={(e) => e.stopPropagation()}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {sku.description}
              </Typography>
              
              <Typography variant="h6" color="primary" fontWeight="bold">
                {formatPrice(sku.unitAmount)}
                {sku.type === 'subscription' && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    /month
                  </Typography>
                )}
                {sku.type === 'metered' && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    /hour
                  </Typography>
                )}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {oneTimeAddOns.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            One-Time Add-Ons
          </Typography>
          <Stack spacing={2}>
            {oneTimeAddOns.map(renderAddOnCard)}
          </Stack>
        </Box>
      )}

      {subscriptionAddOns.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Monthly Services
          </Typography>
          <Stack spacing={2}>
            {subscriptionAddOns.map(renderAddOnCard)}
          </Stack>
        </Box>
      )}

      {meteredAddOns.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            On-Demand Services
          </Typography>
          <Stack spacing={2}>
            {meteredAddOns.map(renderAddOnCard)}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default AddOnSelector;
